import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet } from 'react-native';
import Animated, { SlideInDown, SlideOutDown } from 'react-native-reanimated';

import { theme } from '@garnish/constants';
import { Alert, IconLink, useResponsive } from '@garnish/core';

// ─────────────────────────────────────────────────────────────────────────────

export const FloatingNotice = (props: Props) => {
  const { text, palette = 'info', onClose } = props;

  const { formatMessage } = useIntl();
  const { minWidth } = useResponsive();

  // ─── Styles ────────────────────────────────────────────────────────────────

  const containerStyles = [styles.container, minWidth.isSM ? styles.containerSM : undefined];

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <Animated.View
      style={containerStyles}
      entering={ENTERING_ANIMATION}
      exiting={EXITING_ANIMATION}
    >
      <Alert.Container palette={palette} size="lg" role="alert" hasBorder style={styles.notice}>
        <Alert.Text style={styles.text}>{text}</Alert.Text>

        <IconLink
          name="IconBadgeClose"
          hitSlop={theme.spacing['3']}
          color={theme.colors.DARK_KALE}
          onPress={onClose}
          accessibilityLabel={formatMessage(messages.closeA11yLabel)}
        />
      </Alert.Container>
    </Animated.View>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  closeA11yLabel: {
    defaultMessage: 'Dismiss notice',
    description: 'Floating notice | Close button | Accessibility label',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

export const FLOATING_NOTICE_PALETTES = ['info', 'success', 'caution'] as const;

const ENTERING_DELAY = 500;
const ANIMATION_DURATION = 1000;
const ENTERING_ANIMATION = SlideInDown.delay(ENTERING_DELAY).duration(ANIMATION_DURATION);
const EXITING_ANIMATION = SlideOutDown.duration(ANIMATION_DURATION);

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    borderRadius: theme.radius.large,
    backgroundColor: theme.colors.APP_BACKGROUND,
    boxShadow: '0px 0px 20px 0px rgba(14, 21, 14, 0.05)',
  },
  containerSM: {
    alignSelf: 'center',
    width: '100%',
    maxWidth: 400,
    marginBottom: theme.spacing['6'],
    boxShadow: '0px 8px 40px 4px rgba(14, 21, 14, 0.14)',
  },
  notice: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  text: {
    flex: 1,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  text: string;
  palette?: (typeof FLOATING_NOTICE_PALETTES)[number];
  onClose: () => void;
};
